import {
    EventId,
    EventInviteeId,
    ReferenceContactId,
    RsvpResponse,
    rsvpResponseLabels,
    rsvpResponses,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { EventInviteeDto } from "components/calendar/calendar-types";
import {
    bulkAddedNotice,
    bulkAddFailedNotice,
    formatInviteeName,
    invitationResultText,
    inviteeAddedNotice,
    inviteeAlreadyListedNotice,
    InviteePickerNotice,
    inviteesEmptyStateText,
    InviteeSummaryCounts,
    inviteFlowExplainerText,
    rosterSummaryCounts,
} from "components/calendar/invitee-copy";
import { SelectedReferenceContact } from "components/references/select-reference/reference-contact-picker";
import { trpc } from "components/rpc/trpc";
import { useCopyToClipboard } from "components/shared/hooks";
import { DeleteButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { ChoiceTileOption, ChoiceTiles } from "components/ui/choice-tiles";
import { ReferenceContactLink } from "components/ui/entity-link";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useState } from "react";
import { EventInviteePicker } from "./event-invitee-picker";
import { EventSection } from "./event-section";

const rsvpBadgeTypes: Record<RsvpResponse, "success" | "error" | "info"> = {
    yes: "success",
    no: "error",
    maybe: "info",
};

const responseOptions: ChoiceTileOption<RsvpResponse>[] = rsvpResponses.map(response => ({
    key: response,
    title: rsvpResponseLabels[response],
}));

const guestRsvpUrl = (rsvpCode: string): string =>
    `${window.location.origin}/rsvp/${rsvpCode}`;

const formatInvitedAt = (invitedAt: string): string =>
    new Date(invitedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });

interface EventRosterProps {
    eventId: EventId;
}

export const EventRoster: React.FC<EventRosterProps> = props => {
    const { eventId } = props;
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.event.listInvitees.queryOptions({ id: eventId }),
    });
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerNotice, setPickerNotice] = useState<InviteePickerNotice | null>(null);
    const [editingRsvpId, setEditingRsvpId] = useState<EventInviteeId | null>(null);

    const addMutation = useMutation({
        ...trpcClient.event.addInvitees.mutationOptions(),
        onSuccess: async () => {
            await query.refetch();
        },
    });
    const removeMutation = useMutation({
        ...trpcClient.event.removeInvitee.mutationOptions(),
        onSuccess: async () => {
            await query.refetch();
        },
    });
    const markMutation = useMutation({
        ...trpcClient.event.markInvited.mutationOptions(),
        onSuccess: async () => {
            await query.refetch();
        },
    });
    const sendMutation = useMutation({
        ...trpcClient.event.sendInvitations.mutationOptions(),
        onSuccess: async () => {
            await query.refetch();
        },
    });

    const handlePicked = useCallback(
        (selected: SelectedReferenceContact) => {
            addMutation.mutate(
                {
                    id: eventId,
                    invitees: [
                        {
                            entityType: "contact" as const,
                            entityId: selected.referenceContactId,
                            contactId: selected.contactId,
                        },
                    ],
                },
                {
                    onSuccess: result => {
                        setPickerNotice(
                            result.added === 0
                                ? inviteeAlreadyListedNotice(selected.name)
                                : inviteeAddedNotice(selected.name),
                        );
                    },
                },
            );
        },
        [addMutation, eventId],
    );

    const handleBulkAdd = useCallback(
        async (selected: SelectedReferenceContact[]) => {
            if (selected.length === 0) {
                setPickerNotice(bulkAddedNotice(0, 0));
                return;
            }
            try {
                const result = await addMutation.mutateAsync({
                    id: eventId,
                    invitees: selected.map(entry => ({
                        entityType: "contact" as const,
                        entityId: entry.referenceContactId,
                        contactId: entry.contactId,
                    })),
                });
                setPickerNotice(bulkAddedNotice(result.added, selected.length));
            } catch {
                setPickerNotice(bulkAddFailedNotice());
            }
        },
        [addMutation, eventId],
    );

    const handleOpenPicker = useCallback(() => {
        setPickerNotice(null);
        setPickerOpen(true);
    }, []);
    const handleClosePicker = useCallback(() => setPickerOpen(false), []);
    const handleMarkInvited = useCallback(
        (inviteeId: EventInviteeId) => {
            markMutation.mutate({ id: eventId, inviteeIds: [inviteeId] });
        },
        [markMutation, eventId],
    );
    const handleRemove = useCallback(
        (inviteeId: EventInviteeId) => {
            removeMutation.mutate({ inviteeId });
        },
        [removeMutation],
    );
    const handleStartRsvp = useCallback(
        (inviteeId: EventInviteeId) => setEditingRsvpId(inviteeId),
        [],
    );
    const handleCloseRsvp = useCallback(() => setEditingRsvpId(null), []);
    const handleChanged = useCallback(() => query.refetch(), [query]);

    const invitees = query.data ?? [];
    const emailableIds = invitees
        .filter(invitee => invitee.invitedAt === null && invitee.hasEmail)
        .map(invitee => invitee.id);
    const handleSendInvitations = useCallback(() => {
        sendMutation.mutate({ id: eventId, inviteeIds: emailableIds });
    }, [sendMutation, eventId, emailableIds]);

    return (
        <EventSection
            title="Guest list"
            actions={
                <div className="flex flex-wrap items-center gap-2">
                    {emailableIds.length > 0 ? (
                        <SecondaryButton
                            onClick={handleSendInvitations}
                            disabled={sendMutation.isPending}>
                            Send email invitations
                        </SecondaryButton>
                    ) : null}
                    <PrimaryButton onClick={handleOpenPicker}>Add invitees</PrimaryButton>
                </div>
            }>
            <RsvpSummaryLine counts={rosterSummaryCounts(invitees)} />
            <p className="mt-1 text-xs text-gray-500">{inviteFlowExplainerText}</p>
            {sendMutation.isSuccess ? (
                <div className="mt-2">
                    <InlineBanner type="success">
                        {invitationResultText(sendMutation.data)}
                    </InlineBanner>
                </div>
            ) : null}
            {addMutation.isError ||
            removeMutation.isError ||
            markMutation.isError ||
            sendMutation.isError ? (
                <div className="mt-2">
                    <InlineBanner type="error">
                        Could not update the guest list. Please try again.
                    </InlineBanner>
                </div>
            ) : null}
            <div className="mt-2">
                {query.isPending ? <Spinner /> : null}
                {query.isError ? (
                    <p className="text-sm text-red-600">Could not load the guest list.</p>
                ) : null}
                {query.data && query.data.length === 0 ? (
                    <p className="text-sm text-gray-500">{inviteesEmptyStateText}</p>
                ) : null}
                {invitees.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead>
                                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    <th className="py-2 pr-4">Invitee</th>
                                    <th className="py-2 pr-4">Invited</th>
                                    <th className="py-2 pr-4">RSVP</th>
                                    <th className="py-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {invitees.map(invitee => (
                                    <RosterRow
                                        key={invitee.id}
                                        invitee={invitee}
                                        editingRsvp={editingRsvpId === invitee.id}
                                        markPending={markMutation.isPending}
                                        onMarkInvited={handleMarkInvited}
                                        onStartRsvp={handleStartRsvp}
                                        onCloseRsvp={handleCloseRsvp}
                                        onChanged={handleChanged}
                                        onRemove={handleRemove}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </div>
            <Panel
                isOpen={pickerOpen}
                headerText="Add invitees"
                type={PanelType.extraLarge}
                onDismiss={handleClosePicker}>
                <div className="space-y-3 p-4">
                    {pickerNotice ? (
                        <InlineBanner type={pickerNotice.type}>
                            {pickerNotice.text}
                        </InlineBanner>
                    ) : null}
                    <EventInviteePicker
                        onSelect={handlePicked}
                        onBulkAdd={handleBulkAdd}
                    />
                </div>
            </Panel>
        </EventSection>
    );
};

const RsvpSummaryLine: React.FC<{ counts: InviteeSummaryCounts }> = props => {
    const { counts } = props;
    if (counts.total === 0) {
        return null;
    }
    const noReply = counts.total - counts.yes - counts.maybe - counts.no;
    const stats = [
        { label: "yes", value: counts.yes, className: "text-green-700" },
        { label: "maybe", value: counts.maybe, className: "text-gray-700" },
        { label: "no", value: counts.no, className: "text-red-700" },
        { label: "no reply", value: noReply, className: "text-gray-500" },
    ];
    return (
        <p className="mt-1 text-sm text-gray-500">
            {counts.total} {counts.total === 1 ? "guest" : "guests"}
            {stats
                .filter(stat => stat.value > 0)
                .map(stat => (
                    <span key={stat.label}>
                        {" · "}
                        <span className={stat.className}>
                            {stat.value} {stat.label}
                        </span>
                    </span>
                ))}
        </p>
    );
};

interface RosterRowProps {
    invitee: EventInviteeDto;
    editingRsvp: boolean;
    markPending: boolean;
    onMarkInvited: (inviteeId: EventInviteeId) => void;
    onStartRsvp: (inviteeId: EventInviteeId) => void;
    onCloseRsvp: () => void;
    onChanged: () => Promise<unknown>;
    onRemove: (inviteeId: EventInviteeId) => void;
}

const RosterRow: React.FC<RosterRowProps> = props => {
    const {
        invitee,
        editingRsvp,
        markPending,
        onMarkInvited,
        onStartRsvp,
        onCloseRsvp,
        onChanged,
        onRemove,
    } = props;
    const displayName = formatInviteeName(invitee.name);
    const [copy, isCopied] = useCopyToClipboard();
    return (
        <>
            <tr>
                <td className="py-2.5 pr-4 align-top">
                    {invitee.entityType === "contact" ? (
                        <ReferenceContactLink
                            referenceContactId={invitee.entityId as ReferenceContactId}
                            name={displayName}
                            className="font-medium"
                        />
                    ) : (
                        <p className="text-sm font-medium text-gray-900">{displayName}</p>
                    )}
                    {invitee.businessName ? (
                        <p className="text-xs text-gray-500">{invitee.businessName}</p>
                    ) : null}
                </td>
                <td className="py-2.5 pr-4 align-top">
                    {invitee.invitedAt === null ? (
                        <span className="text-sm text-gray-500">
                            {invitee.hasEmail
                                ? "Not yet invited"
                                : "Not yet invited · no email"}
                        </span>
                    ) : (
                        <span className="text-sm text-gray-900">
                            {formatInvitedAt(invitee.invitedAt)}
                        </span>
                    )}
                </td>
                <td className="py-2.5 pr-4 align-top">
                    {invitee.rsvpResponse ? (
                        <Badge
                            type={rsvpBadgeTypes[invitee.rsvpResponse]}
                            value={rsvpResponseLabels[invitee.rsvpResponse]}
                        />
                    ) : (
                        <span className="text-sm text-gray-500">No reply</span>
                    )}
                    {invitee.rsvpResponse && invitee.rsvpSource === "advisor" ? (
                        <span className="ml-1 text-xs text-gray-500">
                            recorded by {invitee.rsvpRecordedByName ?? "advisor"}
                        </span>
                    ) : null}
                    {invitee.rsvpNote ? (
                        <p className="mt-0.5 max-w-56 truncate text-xs text-gray-500">
                            {invitee.rsvpNote}
                        </p>
                    ) : null}
                </td>
                <td className="py-2.5 text-right align-top">
                    <div className="flex flex-wrap items-center justify-end gap-1">
                        <SecondaryCompactButton
                            onClick={() => copy(guestRsvpUrl(invitee.rsvpCode))}>
                            {isCopied ? "Link copied" : "Copy RSVP link"}
                        </SecondaryCompactButton>
                        {invitee.invitedAt === null ? (
                            <SecondaryCompactButton
                                disabled={markPending}
                                onClick={() => onMarkInvited(invitee.id)}>
                                Mark invited
                            </SecondaryCompactButton>
                        ) : null}
                        <SecondaryCompactButton onClick={() => onStartRsvp(invitee.id)}>
                            {invitee.rsvpResponse ? "Edit RSVP" : "Record RSVP"}
                        </SecondaryCompactButton>
                        <DeleteButton
                            backgroundColor="bg-white"
                            confirm={{
                                title: "Remove invitee",
                                message: `Remove ${displayName} from this event?`,
                            }}
                            onClick={() => onRemove(invitee.id)}
                        />
                    </div>
                </td>
            </tr>
            {editingRsvp ? (
                <tr>
                    <td colSpan={4} className="pb-3">
                        <InviteeRsvpEditor
                            invitee={invitee}
                            onClose={onCloseRsvp}
                            onSaved={onChanged}
                        />
                    </td>
                </tr>
            ) : null}
        </>
    );
};

interface InviteeRsvpEditorProps {
    invitee: EventInviteeDto;
    onClose: () => void;
    onSaved: () => Promise<unknown>;
}

const InviteeRsvpEditor: React.FC<InviteeRsvpEditorProps> = props => {
    const { invitee, onClose, onSaved } = props;
    const trpcClient = trpc.useTRPC();
    const [response, setResponse] = useState<RsvpResponse | null>(invitee.rsvpResponse);
    const [note, setNote] = useState(invitee.rsvpNote ?? "");
    const mutation = useMutation({
        ...trpcClient.event.recordRsvp.mutationOptions(),
        onSuccess: async () => {
            await onSaved();
            onClose();
        },
    });
    const handleSave = useCallback(() => {
        if (response === null) {
            return;
        }
        mutation.mutate({
            inviteeId: invitee.id,
            response,
            note: note.trim() === "" ? null : note,
        });
    }, [mutation, invitee.id, response, note]);
    return (
        <div className="space-y-3 rounded-lg border border-gray-200 p-3">
            {mutation.isError ? (
                <InlineBanner type="error">
                    Could not save the RSVP. Please try again.
                </InlineBanner>
            ) : null}
            <ChoiceTiles
                options={responseOptions}
                columns={3}
                value={response}
                onSelect={(key: RsvpResponse) => setResponse(key)}
            />
            <TextArea
                label="Note"
                rows={2}
                value={note}
                onChange={e => setNote(e.target.value)}
            />
            <div className="flex justify-end gap-2">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <PrimaryButton
                    onClick={handleSave}
                    disabled={response === null || mutation.isPending}>
                    Save RSVP
                </PrimaryButton>
            </div>
        </div>
    );
};
